import React from 'react';
import type { StorageProvider, StorageStatus } from '../../services/storageService';
import StorageProviderTile, { type StorageTileMode } from './StorageProviderTile';
import { getVisibleProviders, isEligibleForCassetteProjectStorage, isProviderLinked, isProviderLinkedForDisplay } from './storageProviderUi';

export interface StorageProviderGridProps {
  mode: StorageTileMode;
  status: StorageStatus | null;
  projectStorageProvider: StorageProvider;
  eligibleForCassetteProjectStorage?: boolean;
  busyProvider?: StorageProvider | null;
  highlightedProvider?: StorageProvider | null;
  defaultProvider?: StorageProvider | '';
  savingDefault?: boolean;
  onConnect: (provider: StorageProvider) => void;
  onDisconnect: (provider: StorageProvider) => void;
  onCassetteCloudUpsell?: () => void;
  onMoveProjectsHere?: (provider: StorageProvider) => void;
  onSelectDefault?: (provider: StorageProvider) => void;
}

const StorageProviderGrid: React.FC<StorageProviderGridProps> = ({
  mode,
  status,
  projectStorageProvider,
  eligibleForCassetteProjectStorage: eligibleAuthFallback = true,
  busyProvider = null,
  highlightedProvider = null,
  defaultProvider = '',
  savingDefault = false,
  onConnect,
  onDisconnect,
  onCassetteCloudUpsell,
  onMoveProjectsHere,
  onSelectDefault,
}) => {
  const providers = getVisibleProviders(status);
  const ccProjectStorageEligible = isEligibleForCassetteProjectStorage(status, eligibleAuthFallback);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
      {providers.map((provider) => {
        const linked = isProviderLinkedForDisplay(status, provider);
        const provisioned = isProviderLinked(status, provider);
        const isProjectStorage = provider === projectStorageProvider && provisioned;
        const canDisconnect = mode === 'settings' && provisioned && !isProjectStorage;

        return (
          <StorageProviderTile
            key={provider}
            provider={provider}
            mode={mode}
            linked={linked}
            isProjectStorage={isProjectStorage}
            accountEmail={status?.connections?.[provider]?.accountEmail}
            busy={busyProvider === provider}
            highlighted={highlightedProvider === provider}
            defaultSelected={defaultProvider === provider}
            savingDefault={savingDefault}
            cassetteEligible={ccProjectStorageEligible}
            eligibleForProjectStorage={ccProjectStorageEligible}
            onConnect={() => {
              if (provider === 'cassette_s3' && !ccProjectStorageEligible) {
                onCassetteCloudUpsell?.();
                return;
              }
              onConnect(provider);
            }}
            onDisconnect={canDisconnect ? () => onDisconnect(provider) : undefined}
            onSelectDefault={
              mode === 'onboarding' &&
              linked &&
              onSelectDefault &&
              (provider !== 'cassette_s3' || ccProjectStorageEligible)
                ? () => onSelectDefault(provider)
                : undefined
            }
          />
        );
      })}
    </div>
  );
};

export default StorageProviderGrid;
